'use client';

/**
 * ADO metadata hooks — projects, area paths, iterations, identity search.
 * All calls use the Bearer token obtained via useAdoAuth.
 */
import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';

// `||` (not `??`) so a build-time var threaded through as an empty string
// (e.g. an unset Azure Pipelines variable interpolating to "") falls back
// the same as a genuinely unset one, rather than resolving to
// "https://dev.azure.com/" with no org segment.
const ADO_ORG = process.env.NEXT_PUBLIC_ADO_ORG || '3phalliance';
const ADO_BASE = `https://dev.azure.com/${ADO_ORG}`;
const ADO_VSSPS_BASE = `https://vssps.dev.azure.com/${ADO_ORG}`;

// Opt-in scope for the "Push to ADO" project picker — comma-separated project
// names. The org may host many unrelated projects (marketing, sales, other
// teams); left unset, every project the token can see is shown, same as
// before this existed. Set to restrict the picker to the project(s) actually
// relevant to test-plan publishing (Val/Norma today; per-persona defaults
// can layer on top of this later without another env var).
const ADO_TESTPLAN_PROJECTS_ALLOWLIST = process.env.NEXT_PUBLIC_ADO_TESTPLAN_PROJECTS ?? '';

/** Filter a project list down to an explicit allowlist (comma-separated,
 * case-insensitive project names). An empty/unset allowlist returns the
 * list unchanged — this narrows an otherwise-full list, it never expands one. */
export function filterProjectsByAllowlist(projects: AdoProject[], allowlistCsv: string): AdoProject[] {
  const allowlist = allowlistCsv.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  if (allowlist.length === 0) return projects;
  return projects.filter(p => allowlist.includes(p.name.toLowerCase()));
}
function adoHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface AdoProject {
  id: string;
  name: string;
}
export interface AdoIdentity {
  displayName: string;
  uniqueName: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Wire shapes
//
// Each interface below narrows an Azure DevOps REST response to the fields
// this file actually reads — the services return considerably more. They are
// applied by annotating the `await res.json()` result, which is the only
// typing these responses get: nothing validates them at runtime.
// ─────────────────────────────────────────────────────────────────────────────

/** A node of the `wit/classificationnodes/{areas,iterations}` tree. */
interface AdoClassificationNode {
  name: string;
  children?: AdoClassificationNode[];
}

/** `_apis/projects` — a page of projects. */
interface AdoProjectListResponse {
  value?: AdoProject[];
}

/** `_apis/testplan/plans` — a page of test plans. */
interface AdoTestPlanListResponse {
  value?: AdoTestPlan[];
}

/** A suite as `_apis/testplan/Plans/{id}/suites` returns it. */
interface AdoTestSuiteWire {
  id: number;
  name: string;
  suiteType: string;
  /** Absent on the root suite of a plan. */
  parentSuite?: {
    id: number;
  } | null;
}
interface AdoTestSuiteListResponse {
  value?: AdoTestSuiteWire[];
}

/**
 * One entry of `results[].identities[]` from the IdentityPicker.
 *
 * Every field is optional because which ones are populated depends on the
 * identity's source directory (AAD, MSA, Graph) — that is why the mapping
 * below falls through several candidates for both the display name and the
 * address rather than reading one field.
 */
interface AdoIdentityPickerIdentity {
  entityType?: string;
  subjectKind?: string;
  displayName?: string;
  providerDisplayName?: string;
  signInAddress?: string;
  mail?: string;
  uniqueName?: string;
  properties?: {
    Mail?: {
      $value?: string;
    };
  };
}
interface AdoIdentityPickerResponse {
  results?: {
    identities?: AdoIdentityPickerIdentity[];
  }[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Recursively flatten a classification node tree into `Parent\Child\...` paths. */
function flattenNodes(node: AdoClassificationNode, parentPath: string = ''): string[] {
  const fullPath = parentPath ? `${parentPath}\\${node.name}` : node.name;
  const paths: string[] = [fullPath];
  if (node.children && Array.isArray(node.children)) {
    for (const child of node.children) {
      paths.push(...flattenNodes(child, fullPath));
    }
  }
  return paths;
}

/**
 * Join the inputs a resource is keyed on into one comparable string. Each part
 * is length-prefixed rather than delimited, so no separator character has to
 * be assumed absent from a bearer token or an ADO project name, and two
 * different input lists can never collide on the same key.
 */
function adoResourceKey(...parts: Array<string | number>): string {
  return parts.map(p => `${String(p).length}:${p}`).join('');
}

// Stable empty results. A fresh `[]` per render would give every consumer a
// new array identity on every render and defeat the dependency arrays in
// push-to-ado-modal (the suite auto-select effect keys on `suites`).
const NO_PROJECTS: AdoProject[] = [];
const NO_PATHS: string[] = [];
const NO_PLANS: AdoTestPlan[] = [];
const NO_SUITES: AdoTestSuite[] = [];

/** A fetch result tagged with the input key that produced it. */
interface SettledAdoResource<T> {
  key: string;
  value: T;
  error: string | null;
}

/**
 * Shared machinery for the hooks below: fetch some ADO metadata keyed on a
 * token plus zero or more scoping ids, and expose `{ value, loading, error }`.
 *
 * `loading` is *derived* — "the inputs I am being asked about are not the
 * inputs the settled result came from" — rather than held in its own state
 * and flipped on inside the effect. Every one of these hooks used to open its
 * effect with an unconditional `setLoading(true)`, which is a render too late:
 * on a token/project change React renders once pairing the new inputs with the
 * previous `loading: false` before any effect runs. push-to-ado-modal reads
 * exactly that pair — `phase === 'loading-meta' && !projectsLoading` — and so
 * promoted itself straight to `ready` on that stale frame, skipping the
 * metadata-loading state and briefly offering an empty project picker.
 * Deriving closes the window, and removes the synchronous set-state that the
 * React Compiler flags (react-hooks/set-state-in-effect).
 *
 * `load` must be a `useCallback` whose dependencies are exactly the inputs
 * encoded in `key`, so the two change together; a null `key` means "not enough
 * inputs to fetch yet" and settles nothing.
 */
function useAdoResource(key, empty, load) {
  const $ = _c(9);
  const [settled, setSettled] = useState(null);
  let t0;
  let t1;
  if ($[0] !== empty || $[1] !== key || $[2] !== load) {
    t0 = () => {
      if (key === null) {
        return;
      }
      const controller = new AbortController();
      load(controller.signal).then(value => {
        if (!controller.signal.aborted) {
          setSettled({
            key,
            value,
            error: null
          });
        }
      }).catch(err => {
        if (controller.signal.aborted) {
          return;
        }
        setSettled({
          key,
          value: empty,
          error: err.message
        });
      });
      return () => controller.abort();
    };
    t1 = [key, empty, load];
    $[0] = empty;
    $[1] = key;
    $[2] = load;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  const current = settled !== null && settled.key === key ? settled : null;
  const t2 = current ? current.value : empty;
  const t3 = key !== null && current === null;
  const t4 = current ? current.error : null;
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      value: t2,
      loading: t3,
      error: t4
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch the list of ADO projects the token has access to.
 */
export function useAdoProjects(token) {
  const $ = _c(8);
  let t0;
  if ($[0] !== token) {
    t0 = async signal => {
      if (!token) {
        return NO_PROJECTS;
      }
      const res = await fetch(`${ADO_BASE}/_apis/projects?api-version=7.1`, {
        headers: adoHeaders(token),
        signal
      });
      if (!res.ok) {
        throw new Error(`Projects fetch failed: ${res.status}`);
      }
      const data = await res.json();
      return filterProjectsByAllowlist((data.value ?? []).map(_temp), ADO_TESTPLAN_PROJECTS_ALLOWLIST);
    };
    $[0] = token;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const load = t0;
  let t1;
  if ($[2] !== token) {
    t1 = token ? adoResourceKey(token) : null;
    $[2] = token;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    value: projects,
    loading,
    error
  } = useAdoResource(t1, NO_PROJECTS, load);
  let t2;
  if ($[4] !== error || $[5] !== loading || $[6] !== projects) {
    t2 = {
      projects,
      loading,
      error
    };
    $[4] = error;
    $[5] = loading;
    $[6] = projects;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/**
 * Fetch and flatten area paths for a given project.
 * Returns a flat array of `Project\Area\Sub-Area` strings.
 */
function _temp(p) {
  return {
    id: p.id,
    name: p.name
  };
}
export function useAdoAreaPaths(token, project) {
  const $ = _c(10);
  let t0;
  if ($[0] !== project || $[1] !== token) {
    t0 = async signal => {
      if (!token || !project) {
        return NO_PATHS;
      }
      const url = `${ADO_BASE}/${encodeURIComponent(project)}/_apis/wit/classificationnodes/areas?$depth=10&api-version=7.1`;
      const res = await fetch(url, {
        headers: adoHeaders(token),
        signal
      });
      if (!res.ok) {
        throw new Error(`Area paths fetch failed: ${res.status}`);
      }
      const data = await res.json();
      return flattenNodes(data);
    };
    $[0] = project;
    $[1] = token;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const load = t0;
  let t1;
  if ($[3] !== project || $[4] !== token) {
    t1 = token && project ? adoResourceKey(token, project) : null;
    $[3] = project;
    $[4] = token;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    value: paths,
    loading,
    error
  } = useAdoResource(t1, NO_PATHS, load);
  let t2;
  if ($[6] !== error || $[7] !== loading || $[8] !== paths) {
    t2 = {
      paths,
      loading,
      error
    };
    $[6] = error;
    $[7] = loading;
    $[8] = paths;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}

/**
 * Fetch and flatten iteration paths for a given project.
 * Returns a flat array of `Project\Release\Sprint` strings.
 */
export function useAdoIterations(token, project) {
  const $ = _c(10);
  let t0;
  if ($[0] !== project || $[1] !== token) {
    t0 = async signal => {
      if (!token || !project) {
        return NO_PATHS;
      }
      const url = `${ADO_BASE}/${encodeURIComponent(project)}/_apis/wit/classificationnodes/iterations?$depth=5&api-version=7.1`;
      const res = await fetch(url, {
        headers: adoHeaders(token),
        signal
      });
      if (!res.ok) {
        throw new Error(`Iterations fetch failed: ${res.status}`);
      }
      const data = await res.json();
      return flattenNodes(data);
    };
    $[0] = project;
    $[1] = token;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const load = t0;
  let t1;
  if ($[3] !== project || $[4] !== token) {
    t1 = token && project ? adoResourceKey(token, project) : null;
    $[3] = project;
    $[4] = token;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    value: iterations,
    loading,
    error
  } = useAdoResource(t1, NO_PATHS, load);
  let t2;
  if ($[6] !== error || $[7] !== iterations || $[8] !== loading) {
    t2 = {
      iterations,
      loading,
      error
    };
    $[6] = error;
    $[7] = iterations;
    $[8] = loading;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}

/**
 * Search ADO identities by display name or email.
 * Called as the user types — debounce should be applied at the call-site.
 */
export async function searchAdoIdentities(token: string, query: string): Promise<AdoIdentity[]> {
  if (!query.trim()) return [];

  // IdentityPicker requires POST with a JSON body — GET is not supported
  const url = `${ADO_VSSPS_BASE}/_apis/IdentityPicker/Identities?api-version=7.1-preview`;
  const res = await fetch(url, {
    method: 'POST',
    headers: adoHeaders(token),
    body: JSON.stringify({
      query: query.trim(),
      identityTypes: ['user'],
      operationScopes: ['ims', 'source'],
      properties: ['DisplayName', 'Mail'],
      options: {
        MinResults: 4,
        MaxResults: 20
      }
    })
  });
  if (!res.ok) throw new Error(`Identity search failed: ${res.status}`);
  const data: AdoIdentityPickerResponse = await res.json();

  // Response shape: { results: [{ identities: [...] }] }
  const identities = data?.results?.[0]?.identities ?? [];
  return identities.filter(i => i.entityType === 'User' || i.subjectKind === 'user').map(i => ({
    displayName: i.displayName ?? i.providerDisplayName ?? '',
    uniqueName: i.signInAddress ?? i.mail ?? i.properties?.Mail?.['$value'] ?? i.uniqueName ?? ''
  })).filter(i => i.displayName);
}

// ─────────────────────────────────────────────────────────────────────────────
// Test Plans + Suites (for Mode B — import into existing)
// ─────────────────────────────────────────────────────────────────────────────

export interface AdoTestPlan {
  id: number;
  name: string;
}
export interface AdoTestSuite {
  id: number;
  name: string;
  suiteType: string;
  parentSuiteId: number | null;
}

/** Fetch all test plans in a project, following continuation tokens. */
export function useAdoTestPlans(token, project) {
  const $ = _c(9);
  let t0;
  if ($[0] !== project || $[1] !== token) {
    t0 = async signal => {
      if (!token || !project) {
        return NO_PLANS;
      }
      const all = [];
      let url = `${ADO_BASE}/${encodeURIComponent(project)}/_apis/testplan/plans?$top=200&api-version=7.1`;
      while (url) {
        const res = await fetch(url, {
          headers: adoHeaders(token),
          signal
        });
        if (!res.ok) {
          break;
        }
        const data = await res.json();
        all.push(...(data.value ?? []).map(_temp2));
        const next = res.headers.get("x-ms-continuationtoken");
        url = next ? `${ADO_BASE}/${encodeURIComponent(project)}/_apis/testplan/plans?$top=200&continuationToken=${next}&api-version=7.1` : null;
      }
      return all;
    };
    $[0] = project;
    $[1] = token;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const load = t0;
  let t1;
  if ($[3] !== project || $[4] !== token) {
    t1 = token && project ? adoResourceKey(token, project) : null;
    $[3] = project;
    $[4] = token;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    value: plans,
    loading
  } = useAdoResource(t1, NO_PLANS, load);
  let t2;
  if ($[6] !== loading || $[7] !== plans) {
    t2 = {
      plans,
      loading
    };
    $[6] = loading;
    $[7] = plans;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}

/** Fetch all suites for a given test plan. */
function _temp2(p) {
  return {
    id: p.id,
    name: p.name
  };
}
export function useAdoTestSuites(token, project, planId) {
  const $ = _c(11);
  let t0;
  if ($[0] !== planId || $[1] !== project || $[2] !== token) {
    t0 = async signal => {
      if (!token || !project || !planId) {
        return NO_SUITES;
      }
      const res = await fetch(`${ADO_BASE}/${encodeURIComponent(project)}/_apis/testplan/Plans/${planId}/suites?api-version=7.1`, {
        headers: adoHeaders(token),
        signal
      });
      const d = await res.json();
      return (d.value ?? []).map(_temp3);
    };
    $[0] = planId;
    $[1] = project;
    $[2] = token;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const load = t0;
  let t1;
  if ($[4] !== planId || $[5] !== project || $[6] !== token) {
    t1 = token && project && planId ? adoResourceKey(token, project, planId) : null;
    $[4] = planId;
    $[5] = project;
    $[6] = token;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const {
    value: suites,
    loading
  } = useAdoResource(t1, NO_SUITES, load);
  let t2;
  if ($[8] !== loading || $[9] !== suites) {
    t2 = {
      suites,
      loading
    };
    $[8] = loading;
    $[9] = suites;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}

/**
 * Convert a flat suite list into an ordered display list with full breadcrumb paths.
 * Suites are returned in depth-first order; each entry has a `displayName` like
 * "Root Plan > Sprint 1 > Regression" for easy user comprehension.
 */
function _temp3(s) {
  return {
    id: s.id,
    name: s.name,
    suiteType: s.suiteType,
    parentSuiteId: s.parentSuite?.id ?? null
  };
}
export function buildSuiteDisplayList(suites: AdoTestSuite[]): Array<{
  id: number;
  name: string;
  displayName: string;
}> {
  const byId = new Map(suites.map(s => [s.id, s]));
  function getPath(suite: AdoTestSuite): string {
    const parts: string[] = [];
    let current: AdoTestSuite | undefined = suite;
    while (current) {
      parts.unshift(current.name);
      current = current.parentSuiteId != null ? byId.get(current.parentSuiteId) : undefined;
    }
    return parts.join(' › ');
  }

  // DFS walk to maintain tree order
  const childrenOf = new Map<number | null, AdoTestSuite[]>();
  for (const s of suites) {
    const pid = s.parentSuiteId ?? null;
    if (!childrenOf.has(pid)) childrenOf.set(pid, []);
    childrenOf.get(pid)!.push(s);
  }
  const result: Array<{
    id: number;
    name: string;
    displayName: string;
  }> = [];
  function walk(parentId: number | null) {
    for (const s of childrenOf.get(parentId) ?? []) {
      result.push({
        id: s.id,
        name: s.name,
        displayName: `${getPath(s)} (${s.id})`
      });
      walk(s.id);
    }
  }
  walk(null);
  return result;
}